// Dynamic code frames (docs/autocoder-dynamic-frames.md): the pure helpers the
// suggestions page uses. A pipeline's copy of a library frame links back by
// `library_id`; accepting a suggestion appends the code to every linked copy,
// and "Re-code since" recodes that framework on each of them (the recode
// route, POST /pipelines/:id/recode, dry run first).

import type { AutoCoderFramework } from '../types/gotypes'

/** One pipeline framework linked to a library frame. */
export interface LinkedPipelineFrame {
  pipelineId: string
  pipelineName: string
  /** The framework's name on that pipeline: what the recode route names. */
  framework: string
}

interface PipelineLike {
  id: string
  config?: { name?: string; frameworks?: AutoCoderFramework[] | null } | null
}

/** Every framework, across pipelines, linked to the library frame `frameworkId`. */
export function linkedPipelineFrames(pipelines: PipelineLike[], frameworkId: string): LinkedPipelineFrame[] {
  const out: LinkedPipelineFrame[] = []
  for (const p of pipelines) {
    for (const f of p.config?.frameworks ?? []) {
      if (f.library_id && f.library_id === frameworkId) {
        out.push({ pipelineId: p.id, pipelineName: p.config?.name || p.id, framework: f.name })
      }
    }
  }
  return out
}

/** The recode route's dry-run answer (2b contract). */
export interface RecodeEstimate {
  records: number
  estimatedTokens: number
  estimatedCredits: number
  model: string
}

/** The estimates of several pipelines, added up. */
export function sumEstimates(estimates: RecodeEstimate[]): { records: number; estimatedTokens: number; estimatedCredits: number } {
  return estimates.reduce(
    (acc, e) => ({
      records: acc.records + (e.records || 0),
      estimatedTokens: acc.estimatedTokens + (e.estimatedTokens || 0),
      estimatedCredits: acc.estimatedCredits + (e.estimatedCredits || 0),
    }),
    { records: 0, estimatedTokens: 0, estimatedCredits: 0 },
  )
}

/** A yyyy-mm-dd date (an <input type="date"> value) as the start of that day in UTC, RFC3339. */
export function sinceFromDate(date: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined
  return `${date}T00:00:00Z`
}

/** The yyyy-mm-dd date `days` before `now`. */
export function daysAgo(days: number, now: Date = new Date()): string {
  const d = new Date(now.getTime() - days * 24 * 3600 * 1000)
  return d.toISOString().slice(0, 10)
}
